import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/toast'
import {
  assignableRolesFor,
  portalUserToFormValues,
  validateUserForm,
  type UserFormFieldErrors,
} from '@/lib/users'
import { validateSettingsImage } from '@/lib/settingsImageUpload'
import { getApiErrorMessage } from '@/services/api'
import { usersApi } from '@/services/users'
import {
  useCreateUser,
  useUpdateUser,
  useUpdateUserPermissions,
} from '@/hooks/useUsers'
import { useAppSelector } from '@/store/hooks'
import {
  defaultPermissionsForRole,
  type CreateUserRequest,
  type PermissionMatrix as Matrix,
  type PortalUser,
  type UpdateUserRequest,
  type UserRole,
  type UserStatus,
  type WebRole,
} from '@/types'
import type { PendingUserDocument } from '@/components/settings/users/UserDocumentsSection'

interface UseUserFormOptions {
  user: PortalUser | null
  viewerRole: UserRole | undefined
  initialMatrix: Matrix
  onClose: () => void
}

export function useUserForm({
  user,
  viewerRole,
  initialMatrix,
  onClose,
}: UseUserFormOptions) {
  const { t } = useTranslation('settings')
  const isEdit = !!user
  // Self-service edit: a user editing their own account may only change avatar,
  // first/last name and title. Email, role, status and permissions are locked.
  const viewerId = useAppSelector(s => s.auth.user?.id)
  const isSelf = isEdit && !!user && user.id === viewerId

  const create = useCreateUser()
  const update = useUpdateUser()
  const savePermissions = useUpdateUserPermissions()

  const seed = portalUserToFormValues(user)
  const [firstName, setFirstName] = useState(seed.firstName)
  const [lastName, setLastName] = useState(seed.lastName)
  const [email, setEmail] = useState(seed.email)
  const [title, setTitle] = useState(seed.title)
  const [role, setRole] = useState<WebRole | ''>(seed.role)
  const [status, setStatus] = useState<UserStatus>(seed.status)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    user?.avatar ?? null
  )
  const [avatarRemoved, setAvatarRemoved] = useState(false)
  const avatarObjectUrl = useRef<string | null>(null)
  const [matrix, setMatrix] = useState<Matrix>(initialMatrix)
  // Whether the matrix was hand-edited since the last role pre-fill - so a later
  // role change does NOT silently wipe overrides (2.11.3 step 2.1). Edit mode
  // starts from the user's saved matrix, hence "already customized".
  const [customized, setCustomized] = useState(isEdit)
  const [errors, setErrors] = useState<UserFormFieldErrors>({})
  const [pendingDocuments, setPendingDocuments] = useState<
    PendingUserDocument[]
  >([])
  const [uploadingDocs, setUploadingDocs] = useState(false)

  useEffect(() => {
    return () => {
      if (avatarObjectUrl.current) URL.revokeObjectURL(avatarObjectUrl.current)
    }
  }, [])

  function clearError(field: keyof UserFormFieldErrors) {
    setErrors(e => ({ ...e, [field]: '' }))
  }

  function handleRoleChange(next: WebRole) {
    setRole(next)
    clearError('role')
    if (!customized) {
      setMatrix(defaultPermissionsForRole(next))
    } else if (next === 'Operator') {
      // Settings is not editable for Operator; keep role default (no_access).
      setMatrix(prev => ({
        ...prev,
        settings: defaultPermissionsForRole('Operator').settings,
      }))
    }
  }

  function handleResetMatrix() {
    if (role) {
      setMatrix(defaultPermissionsForRole(role))
      setCustomized(false)
    }
  }

  function handleMatrixChange(next: Matrix) {
    // Never persist a custom settings level for Operator in local state.
    if (role === 'Operator') {
      setMatrix({
        ...next,
        settings: defaultPermissionsForRole('Operator').settings,
      })
    } else {
      setMatrix(next)
    }
    setCustomized(true)
  }

  function handleAvatarChange(file: File | null) {
    if (file) {
      const avatarError = validateSettingsImage(file)
      if (avatarError) {
        setErrors(current => ({
          ...current,
          avatar: t(
            `users.validation.avatar${avatarError === 'type' ? 'Type' : 'Size'}`
          ),
        }))
        return
      }
    }

    if (avatarObjectUrl.current) {
      URL.revokeObjectURL(avatarObjectUrl.current)
      avatarObjectUrl.current = null
    }
    clearError('avatar')
    setAvatarFile(file)
    if (file) {
      const url = URL.createObjectURL(file)
      avatarObjectUrl.current = url
      setAvatarPreview(url)
      setAvatarRemoved(false)
    } else {
      setAvatarPreview(user?.avatar ?? null)
      setAvatarRemoved(false)
    }
  }

  function handleRemoveAvatar() {
    if (avatarObjectUrl.current) {
      URL.revokeObjectURL(avatarObjectUrl.current)
      avatarObjectUrl.current = null
    }
    setAvatarFile(null)
    setAvatarPreview(null)
    setAvatarRemoved(!!user?.avatar)
    clearError('avatar')
  }

  const isSaving =
    create.isPending ||
    update.isPending ||
    savePermissions.isPending ||
    uploadingDocs

  const roleOptions = assignableRolesFor(viewerRole)

  async function uploadPendingDocuments(userId: string) {
    if (pendingDocuments.length === 0) return
    setUploadingDocs(true)
    try {
      const remaining = [...pendingDocuments]
      while (remaining.length > 0) {
        const item = remaining[0]
        await usersApi.uploadDocument(userId, item.file)
        remaining.shift()
        setPendingDocuments([...remaining])
      }
    } finally {
      setUploadingDocs(false)
    }
  }

  async function handleSave() {
    const nextErrors = validateUserForm({ firstName, lastName, email, role }, t)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0 || !role) return

    // Self-edit only sends the personal fields; email/role/status/permissions
    // stay untouched (also enforced on the backend).
    const body: Partial<CreateUserRequest> & { remove_avatar?: boolean } =
      isSelf
        ? {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            title: title.trim() || null,
            avatar: avatarFile ?? undefined,
          }
        : {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            title: title.trim() || null,
            role,
            status,
            avatar: avatarFile ?? undefined,
          }
    if (isEdit && avatarRemoved && !avatarFile) {
      body.remove_avatar = true
    }

    try {
      const saved = isEdit
        ? await update.mutateAsync({
            id: user!.id,
            body: body as UpdateUserRequest,
          })
        : await create.mutateAsync(body as CreateUserRequest)

      if (saved?.id && !isSelf) {
        // Operator cannot customize Settings; always send role default (no_access).
        const permissions =
          role === 'Operator'
            ? {
                ...matrix,
                settings: defaultPermissionsForRole('Operator').settings,
              }
            : matrix
        await savePermissions.mutateAsync({ id: saved.id, permissions })
      }

      if (saved?.id && pendingDocuments.length > 0) {
        try {
          await uploadPendingDocuments(saved.id)
        } catch (docErr) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(docErr, t('users.documents.uploadError')),
          })
          // User (and permissions) already saved — close so the operator can
          // reopen Edit to finish any remaining uploads.
          toast({ variant: 'success', title: t('users.toast.saved') })
          onClose()
          return
        }
      }

      toast({ variant: 'success', title: t('users.toast.saved') })
      onClose()
    } catch (e) {
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    }
  }

  return {
    t,
    isEdit,
    isSelf,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    title,
    setTitle,
    role,
    status,
    setStatus,
    avatarPreview,
    handleAvatarChange,
    handleRemoveAvatar,
    matrix,
    errors,
    clearError,
    isSaving,
    roleOptions,
    handleRoleChange,
    handleResetMatrix,
    handleMatrixChange,
    handleSave,
    pendingDocuments,
    setPendingDocuments,
  }
}
